import type {
    MutableToolStepKey,
} from '@toolproof-core/lib/_contract/workflow-overlay';


export type ToolStepBounds = {
    minStepIndex: number;
    maxStepIndex: number;
    minStepKey: MutableToolStepKey;
    maxStepKey: MutableToolStepKey;
};

export function slotFamilyFromSlotInOwner(slotInOwner: string): 'case' | 'cases' {
    return slotInOwner.startsWith('cases/') ? 'cases' : 'case';
}

export function deriveToolStepBounds(args: {
    aToolStepKey: MutableToolStepKey;
    bToolStepKey: MutableToolStepKey;
    stepIndexByStepKey: ReadonlyMap<MutableToolStepKey, number>;
}): ToolStepBounds | null {
    const { aToolStepKey, bToolStepKey, stepIndexByStepKey } = args;
    const aIndex = stepIndexByStepKey.get(aToolStepKey);
    const bIndex = stepIndexByStepKey.get(bToolStepKey);
    if (aIndex == null || bIndex == null) return null;

    const minStepIndex = Math.min(aIndex, bIndex);
    const maxStepIndex = Math.max(aIndex, bIndex);
    if (maxStepIndex <= minStepIndex) return null;

    const minStepKey = minStepIndex === aIndex ? aToolStepKey : bToolStepKey;
    const maxStepKey = maxStepIndex === aIndex ? aToolStepKey : bToolStepKey;

    return {
        minStepIndex,
        maxStepIndex,
        minStepKey,
        maxStepKey,
    };
}

export function upsertMacroStepBounds(args: {
    boundsByMacroStepKey: Map<string, ToolStepBounds>;
    macroStepKey: string;
    nextBounds: ToolStepBounds;
}): void {
    const { boundsByMacroStepKey, macroStepKey, nextBounds } = args;
    const current = boundsByMacroStepKey.get(macroStepKey);
    if (!current) {
        boundsByMacroStepKey.set(macroStepKey, nextBounds);
        return;
    }

    let minStepIndex = current.minStepIndex;
    let maxStepIndex = current.maxStepIndex;
    let minStepKey = current.minStepKey;
    let maxStepKey = current.maxStepKey;

    if (nextBounds.minStepIndex < minStepIndex) {
        minStepIndex = nextBounds.minStepIndex;
        minStepKey = nextBounds.minStepKey;
    }
    if (nextBounds.maxStepIndex > maxStepIndex) {
        maxStepIndex = nextBounds.maxStepIndex;
        maxStepKey = nextBounds.maxStepKey;
    }

    boundsByMacroStepKey.set(macroStepKey, {
        minStepIndex,
        maxStepIndex,
        minStepKey,
        maxStepKey,
    });
}

export function mergeContiguousToolStepBounds(bounds: ToolStepBounds[]): ToolStepBounds[] {
    return bounds
        .toSorted((a, b) => a.minStepIndex - b.minStepIndex)
        .reduce<ToolStepBounds[]>((acc, current) => {
            const previous = acc[acc.length - 1];
            if (!previous) {
                acc.push({ ...current });
                return acc;
            }

            if (current.minStepIndex <= previous.maxStepIndex + 1) {
                if (current.maxStepIndex > previous.maxStepIndex) {
                    previous.maxStepIndex = current.maxStepIndex;
                    previous.maxStepKey = current.maxStepKey;
                }
                if (current.minStepIndex < previous.minStepIndex) {
                    previous.minStepIndex = current.minStepIndex;
                    previous.minStepKey = current.minStepKey;
                }
                return acc;
            }

            acc.push({ ...current });
            return acc;
        }, []);
}